import { useState, useEffect } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { ShoppingBag, Plus, Minus, Trash2, X, ExternalLink, Loader2 } from "lucide-react";
import { useCartStore } from "@/stores/cartStore";
import { useCurrency } from "@/contexts/CurrencyContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface CartDrawerProps {
  children: React.ReactNode;
}

const CartDrawer = ({ children }: CartDrawerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const { items, isLoading, isSyncing, updateQuantity, removeItem, clearCart, syncCart } = useCartStore();
  const { formatPrice, currency, convertPrice } = useCurrency();
  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
  const totalPrice = items.reduce((sum, item) => sum + (parseFloat(item.price.amount) * item.quantity), 0);

  useEffect(() => {
    if (isOpen) syncCart();
  }, [isOpen, syncCart]);

  const handleCheckout = async () => {
    if (items.length === 0) return;
    setIsCheckingOut(true);
    try {
      const lineItems = items.map((item) => {
        const baseAmount = parseFloat(item.price.amount);
        const displayAmount = typeof convertPrice === "function" ? convertPrice(baseAmount) : baseAmount;
        const image = item.product.node.images?.edges?.[0]?.node;
        return {
          variantId: item.variantId,
          productTitle: item.product.node.title,
          variantTitle: item.variantTitle,
          imageUrl: image?.url,
          unitAmount: Number(displayAmount.toFixed(2)),
          currency: currency || item.price.currencyCode || "USD",
          quantity: item.quantity,
        };
      });

      const { data, error } = await supabase.functions.invoke("create-stripe-checkout", {
        body: {
          items: lineItems,
          successUrl: `${window.location.origin}/checkout/success`,
          cancelUrl: `${window.location.origin}/?checkout=cancel`,
        },
      });

      // Try to read the actual error body from the FunctionsHttpError response
      let errMsg = (data as any)?.error || "";
      if (error && (error as any).context?.json) {
        try {
          const body = await (error as any).context.json();
          errMsg = body?.error || errMsg;
        } catch {
          try {
            errMsg = await (error as any).context.text();
          } catch {}
        }
      }
      if (!errMsg && error) errMsg = (error as any).message || "";

      if (/variant not found|variant unavailable/i.test(errMsg)) {
        clearCart();
        toast.error("Some items in your cart are no longer available. Your cart has been cleared, please add them again from the shop.");
        setIsOpen(false);
        return;
      }
      if (error) throw new Error(errMsg || "Checkout failed");
      if (!data?.url) throw new Error("No checkout URL returned");

      window.location.href = data.url;
    } catch (err: any) {
      console.error("Stripe checkout failed", err);
      toast.error("Could not start checkout. Please try again.");
    } finally {
      setIsCheckingOut(false);
    }
  };


  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>{children}</SheetTrigger>
      <SheetContent className="w-full sm:max-w-md bg-background border-border flex flex-col p-4 sm:p-6">
        <SheetHeader className="border-b border-border pb-4 flex-shrink-0">
          <SheetTitle className="font-display text-xl text-foreground flex items-center gap-2">
            <ShoppingBag className="w-5 h-5 text-primary" />
            Shopping Cart ({totalItems})
          </SheetTitle>
        </SheetHeader>

        <div className="flex flex-col flex-1 min-h-0 mt-4">
          {items.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center">
              <ShoppingBag className="w-16 h-16 text-muted-foreground mb-4" />
              <p className="font-body text-muted-foreground">Your cart is empty</p>
              <p className="font-body text-sm text-muted-foreground mt-1">
                Add some products to get started
              </p>
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto space-y-4 pr-2">
                {items.map((item) => {
                  const image = item.product.node.images?.edges?.[0]?.node;
                  return (
                    <div
                      key={item.variantId}
                      className="flex gap-4 p-3 rounded-xl bg-card border border-border/50"
                    >
                      <div className="w-20 h-20 rounded-lg bg-secondary/50 flex items-center justify-center overflow-hidden">
                        {image && (
                          <img
                            src={image.url}
                            alt={image.altText || item.product.node.title}
                            className="w-full h-full object-cover"
                          />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-display font-medium text-foreground truncate">
                          {item.product.node.title}
                        </h4>
                        {item.variantTitle !== "Default Title" && (
                          <p className="text-xs font-body text-muted-foreground">
                            {item.selectedOptions.map(o => o.value).join(' • ')}
                          </p>
                        )}
                        <p className="font-body font-semibold text-foreground mt-1">
                          {formatPrice(parseFloat(item.price.amount))}
                        </p>
                        <div className="flex items-center gap-2 mt-2">
                          <button
                            onClick={() => updateQuantity(item.variantId, item.quantity - 1)}
                            aria-label="Decrease quantity"
                            className="h-9 w-9 flex items-center justify-center rounded-md bg-secondary hover:bg-secondary/80 transition-colors"
                          >
                            <Minus className="w-3.5 h-3.5 text-foreground" />
                          </button>
                          <span className="font-body text-sm text-foreground w-6 text-center">
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => updateQuantity(item.variantId, item.quantity + 1)}
                            aria-label="Increase quantity"
                            className="h-9 w-9 flex items-center justify-center rounded-md bg-secondary hover:bg-secondary/80 transition-colors"
                          >
                            <Plus className="w-3.5 h-3.5 text-foreground" />
                          </button>
                          <button
                            onClick={() => removeItem(item.variantId)}
                            aria-label="Remove item"
                            className="ml-auto h-9 w-9 flex items-center justify-center rounded-md text-muted-foreground hover:text-destructive transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="border-t border-border pt-4 mt-4 space-y-3 flex-shrink-0 safe-bottom">
                <div className="flex items-center justify-between">
                  <span className="font-body text-muted-foreground">Subtotal</span>
                  <span className="font-display text-xl font-semibold text-foreground">
                    {formatPrice(totalPrice)}
                  </span>
                </div>
                <Button
                  variant="hero"
                  size="lg"
                  className="w-full gap-2"
                  onClick={handleCheckout}
                  disabled={isLoading || isSyncing || isCheckingOut}
                >
                  {isLoading || isSyncing || isCheckingOut ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <ExternalLink className="w-4 h-4" />
                  )}
                  Checkout
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full text-muted-foreground"
                  onClick={clearCart}
                >
                  <X className="w-4 h-4 mr-2" />
                  Clear Cart
                </Button>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
